<template>
    <div class="database-tools">
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="me-2">mdi-database</v-icon>
                SQL-Konsole
              </v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="sqlQuery"
                  label="SQL-Query"
                  variant="outlined"
                  rows="4"
                  placeholder="SELECT * FROM users LIMIT 10"
                ></v-textarea>
                
                <div class="d-flex mb-4">
                  <v-btn 
                    color="primary" 
                    @click="executeQuery"
                    :loading="loading"
                    :disabled="!sqlQuery"
                    class="me-2"
                  >
                    <v-icon start>mdi-play</v-icon>
                    Ausführen
                  </v-btn>
                  <v-btn 
                    color="secondary" 
                    @click="clearQuery"
                    :disabled="!sqlQuery"
                  >
                    <v-icon start>mdi-delete</v-icon>
                    Löschen
                  </v-btn>
                </div>
                
                <v-alert v-if="queryResult && queryResult.error" type="error" class="mb-4">
                  {{ queryResult.error }}
                </v-alert>
                
                <v-alert type="warning" class="mb-4">
                  Sicherheitshinweis: Diese Konsole ist nur für Entwickler und Administratoren zugänglich.
                  Alle SQL-Abfragen werden protokolliert.
                </v-alert>
                
                <div v-if="queryResult && queryResult.data">
                  <div class="d-flex align-center mb-4">
                    <h3 class="text-h6 me-2">Ergebnisse</h3>
                    <v-chip color="primary" size="small">{{ queryResult.rowCount }} Zeilen</v-chip>
                    <v-chip color="secondary" size="small" class="ms-2">{{ queryResult.time }}ms</v-chip>
                  </div>
                  
                  <v-table>
                    <thead>
                      <tr>
                        <th v-for="(column, index) in queryResult.columns" :key="index">
                          {{ column }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rowIndex) in queryResult.data" :key="rowIndex">
                        <td v-for="(column, colIndex) in queryResult.columns" :key="colIndex">
                          {{ row[column] }}
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </template>
  
  <script setup>
  import { ref } from 'vue';
  import { useToast } from 'vue-toastification';
  
  const toast = useToast();
  const sqlQuery = ref('');
  const loading = ref(false);
  const queryResult = ref(null);
  
  const executeQuery = async () => {
    if (!sqlQuery.value.trim()) {
      toast.error('Bitte geben Sie eine SQL-Query ein');
      return;
    }
    
    loading.value = true;
    
    try {
      // Simulierte Antwort (in der Produktionsversion würde hier ein API-Aufruf erfolgen)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Beispielantwort
      if (sqlQuery.value.toLowerCase().includes('select')) {
        queryResult.value = {
          data: [
            { id: 1, loginname: 'admin', vorname: 'Admin', nachname: 'User' },
            { id: 2, loginname: 'user1', vorname: 'Max', nachname: 'Mustermann' },
            { id: 3, loginname: 'user2', vorname: 'Erika', nachname: 'Musterfrau' }
          ],
          columns: ['id', 'loginname', 'vorname', 'nachname'],
          rowCount: 3,
          time: 12
        };
        toast.success('Abfrage erfolgreich ausgeführt');
      } else {
        queryResult.value = {
          error: 'Nur SELECT-Abfragen sind in dieser Demo erlaubt'
        };
        toast.error('Nur SELECT-Abfragen sind in dieser Demo erlaubt');
      }
    } catch (error) {
      toast.error(`Fehler bei der Ausführung: ${error.message}`);
      queryResult.value = {
        error: error.message
      };
    } finally {
      loading.value = false;
    }
  };
  
  const clearQuery = () => {
    sqlQuery.value = '';
    queryResult.value = null;
  };
  </script>
  
  <style scoped>
  .database-tools {
    padding: 16px;
  }
  </style>